import { FiRefreshCw } from 'react-icons/fi'

export interface DropdownSearchHeaderProps {
  searchTerm: string
  onSearchChange: (value: string) => void
  onRefresh: (e?: React.MouseEvent) => void
  isRefreshing?: boolean
  placeholder?: string
  isRTL?: boolean
  /** Optional keydown passthrough on the search input (e.g. Enter-to-add-new). */
  onSearchKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  /** Extra controls after the refresh button (e.g. "New" action). */
  trailingActions?: React.ReactNode
  inputClassName?: string
  className?: string
  autoFocus?: boolean
}

/**
 * Unified search row for lookup dropdowns: search input + refresh button.
 * Keeps search text when refreshing; parent should not clear searchTerm on refresh.
 */
export default function DropdownSearchHeader({
  searchTerm,
  onSearchChange,
  onRefresh,
  isRefreshing = false,
  placeholder,
  isRTL = false,
  onSearchKeyDown,
  trailingActions,
  inputClassName,
  className = 'p-2 border-b border-gray-200',
  autoFocus = true,
}: DropdownSearchHeaderProps) {
  const defaultPlaceholder = isRTL ? 'بحث...' : 'Search...'

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder={placeholder || defaultPlaceholder}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={onSearchKeyDown}
          className={
            inputClassName ||
            `flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              isRTL ? 'text-right' : 'text-left'
            }`
          }
          autoFocus={autoFocus}
        />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onRefresh(e)
          }}
          disabled={isRefreshing}
          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
          title={isRTL ? 'تحديث القائمة' : 'Refresh list'}
          aria-label={isRTL ? 'تحديث القائمة' : 'Refresh list'}
        >
          <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
        {trailingActions}
      </div>
    </div>
  )
}
